import type { ReactNode } from 'react'
import { MinimalButton } from '@ovasabi/ui-minimal'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { useDragScroll } from '../../hooks/useDragScroll'

/* ── FilterPills ─────────────────────────────────────────────────────── */

const PillStyle = {
  Row: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    min-height: 44px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  Pill: styled(MinimalButton)`
    flex-shrink: 0;
    cursor: pointer;
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    padding: 8px 16px;
    min-height: ${minimalVars.control.minTargetSize};
    border-radius: ${minimalVars.radius.pill};
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease, transform 120ms ease;
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textSecondary};
    border: 1px solid ${minimalVars.color.borderSubtle};
    &[data-active] {
      background: ${minimalVars.color.brand};
      color: ${minimalVars.color.textInverse};
      border: 1px solid ${minimalVars.color.brand};
    }

    &:hover:not([aria-pressed="true"]) {
      background: ${minimalVars.color.bgSurfaceHover};
      color: ${minimalVars.color.textPrimary};
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: 2px;
    }
  `,
}

export interface PillOption<T extends string> {
  value: T
  label: ReactNode
}

export interface FilterPillsProps<T extends string> {
  value: T
  options: readonly PillOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
}

/** Horizontally scrollable pill filter with a solid-accent active state. */
export const FilterPills = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: FilterPillsProps<T>) => {
  const dragScroll = useDragScroll()
  return (
    <PillStyle.Row role="group" aria-label={ariaLabel} {...dragScroll}>
      {options.map((option) => (
        <PillStyle.Pill variant="quiet"
          key={option.value}
          type="button"
          data-active={option.value === value || undefined}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </PillStyle.Pill>
      ))}
    </PillStyle.Row>
  )
}

/* ── Tabs (underline) ────────────────────────────────────────────────── */

const TabStyle = {
  /* Four tabs at phone width do not fit a fixed gap, and a wrapped label
     ("Top / Up") reads as two broken tabs rather than one. The row scrolls
     instead, each label stays on one line, and the gap shrinks first. */
  Row: styled.div`
    display: flex;
    gap: ${minimalVars.space.sm};
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }

    /* 420px is below the hand stop, so it stays hand-typed. */
    @media (min-width: 420px) {
      gap: ${minimalVars.space.md};
    }
  `,
  Tab: styled(MinimalButton)<{ $active: boolean }>`
    cursor: pointer;
    font: inherit;
    flex: 0 0 auto;
    white-space: nowrap;
    background: transparent;
    border: 0;
    padding: 10px 2px;
    margin-bottom: -1px;
    font-size: ${minimalVars.typography.bodySize};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.textTertiary)};
    border-bottom: 2px solid ${({ $active }) => ($active ? minimalVars.color.brand : 'transparent')};
    transition: color 160ms ease, border-color 160ms ease;

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: 2px;
    }
  `,
}

export interface TabsProps<T extends string> {
  value: T
  options: readonly PillOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
}

/** Underline tab row (Stores/Buy Again, Overview/Top Up/…). */
export const Tabs = <T extends string>({ value, options, onChange, ariaLabel }: TabsProps<T>) => (
  <TabStyle.Row role="tablist" aria-label={ariaLabel}>
    {options.map((option) => (
      <TabStyle.Tab variant="quiet"
        key={option.value}
        type="button"
        role="tab"
        aria-selected={option.value === value}
        $active={option.value === value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </TabStyle.Tab>
    ))}
  </TabStyle.Row>
)

/* ── SectionHeading ──────────────────────────────────────────────────── */

const SectionStyle = {
  Row: styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  /* The one canonical section/card header. Single size from the h2 token, so
     every peer-level heading across the app is identical — no per-call variance. */
  Title: styled.h2`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.h2Size};
    line-height: 1.15;
    letter-spacing: -0.01em;
    color: ${minimalVars.color.textPrimary};
  `,
  Action: styled(MinimalButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 0 4px;
    margin-top: -3px;
    cursor: pointer;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.brand};
    white-space: nowrap;

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: 2px;
    }
  `,
}

export interface SectionHeadingProps {
  title: ReactNode
  actionLabel?: string
  onAction?: () => void
}

export const SectionHeading = ({ title, actionLabel, onAction }: SectionHeadingProps) => (
  <SectionStyle.Row>
    <SectionStyle.Title>{title}</SectionStyle.Title>
    {actionLabel ? (
      <SectionStyle.Action variant="quiet" type="button" onClick={onAction}>
        {actionLabel} →
      </SectionStyle.Action>
    ) : null}
  </SectionStyle.Row>
)
